// This is a part of the Cadcorp SIS source code. 
// Copyright (c) NEC Software Solutions UK Limited 1991-2026 
import {Overlay} from './Overlay';
import { IAjax, IKeyValue } from '../../../../CommonScripts/Abstractions';
/**
* A factory to get overlays served from a GeognoSIS http interface
* @public
*/
export class OverlayFactory {
    /**
    * @public
    * @property {Overlay[]} overlays The overlays
    */
    overlays:Overlay[];
    /**
     * @property {string|null} sessionId The current session id
     * @public
     * @readonly
     */
    readonly sessionId: string;
    /**
     * @property {IAjax} ajaxWrapper An IAjax implementation to perform asynchronous requests
     * @public
     * @readonly
     */
    readonly ajaxWrapper: IAjax;
    /**
     * @property {string|null} baseUrl The base url for the application
     * @public
     * @readonly
     */
    readonly baseUrl: string;
    /**
    * @property {string} mapName - Current map name
    * @public
    * @readonly 
    */
    readonly mapName: string;
    /**
     * @property {string} layerId The unique id for the swd this item belongs to
     * @public
     */
    readonly layerId: string;
    /**
    * Initialises a new instance of the overlay factory
    * @constructor
    * @public
    * @param {string} sessionId The GeognoSIS session id
    * @param {IAjax} ajaxWrapper Abstraction to perform ajax queries
    * @param {string} baseUrl base url for the application
    * @param {string} mapName the name of the map
    * @param {string} layerId the id of the geognosis layer
    */
    constructor(sessionId: string, ajaxWrapper: IAjax, baseUrl:string, mapName:string, layerId:string) {
        this.overlays = null;
        this.sessionId = sessionId;
        this.ajaxWrapper = ajaxWrapper;
        this.baseUrl = baseUrl;
        this.mapName = mapName;
        this.layerId = layerId;
    }
    /**
     * Gets the layers from the swd
     */
    getOverlays() : Promise<Overlay[]> {
        if (this.overlays === null) {

            //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlays"
            let url = `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlays`;
            return this.ajaxWrapper.get(url).
                then(this.processLayerList.bind(this));
        }
        else {
            return Promise.resolve(this.overlays);
        }

    }
    /**
     * Creates an internal layer
     * @param {object} properties The properties to set on the new overlay
     */
    createInternalLayer(properties: IKeyValue[], name:string): Promise<Overlay> {
        //GeognoSis/{mapName}/{layerId:Guid}/Sessions/{sessionId}/Overlay/new"
        var url = `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/new?name=${name}`;
        let layerIndex = -1;
        return this.ajaxWrapper.post(url, null, null).
            then((response: {data: number}) => {
                layerIndex = response.data;
                url = `${this.baseUrl}GeognoSis/${this.mapName}/${this.layerId}/Sessions/${this.sessionId}/Overlay/${response.data}`;
                var propertiesObject = {};
                if (properties != null && properties.length > 0) {
                    for (var count = 0; count < properties.length; count++) {
                        var tmp = properties[count];
                        propertiesObject[tmp.key] = tmp.value;
                    }
                }
                var propMap = {"PropertyMap": propertiesObject}
                return this.ajaxWrapper.patch(url, propMap, null);
            })
            .then(() => {
                var overlay = new Overlay(this.baseUrl,
                    this.sessionId,
                    layerIndex,
                    this.ajaxWrapper,
                    this.layerId,
                    this.mapName);

                return overlay.populate(null);
            });
    }

    /**
     * Processes the array of overlay indicies from the server
     * @param {object} response The response from the server, an array of overlay indicies
     */
    private processLayerList(response) : Promise<any> {
        var oArray = response.data;
        var pArray = [];
        this.overlays = [];
        for (var c = 0; c < oArray.length; c++) {
            var index = oArray[c];

            var overlay = new Overlay(this.baseUrl,
                this.sessionId,
                index,
                this.ajaxWrapper,
                this.layerId,
                this.mapName);

            pArray.push(overlay.populate(null));
            this.overlays[c] = overlay;
        }
        return Promise.all(pArray);
    }
}

